import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Alert,
  Snackbar,
  CircularProgress,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { enableWhatsApp, disableWhatsApp } from '../../services/whatsappService';
import SaveIcon from '@mui/icons-material/Save';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import QrCodeIcon from '@mui/icons-material/QrCode';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PhoneIcon from '@mui/icons-material/Phone';

export default function WhatsAppSettingsPanel({ settings, setSettings }) {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [displayName, setDisplayName] = useState(settings?.company_name || '');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  // Use the actual WhatsApp settings from the provider settings
  const whatsappEnabled = settings?.whatsapp_enabled || false;
  const virtualNumber = settings?.whatsapp_virtual_number || '';

  const handleEnableWhatsApp = async () => {
    if (!displayName.trim()) {
      setSnackbar({
        open: true,
        message: 'Please enter a display name for WhatsApp',
        severity: 'error'
      });
      return;
    }
    
    setDialogOpen(false);
    setLoading(true);
    
    try {
      const response = await enableWhatsApp(currentUser.uid, {
        display_name: displayName
      });
      
      if (response.data.success) {
        // Update the local settings
        setSettings({
          ...settings,
          whatsapp_enabled: true,
          whatsapp_virtual_number: response.data.virtual_number,
          whatsapp_display_name: displayName
        });
        
        setSnackbar({
          open: true,
          message: 'WhatsApp successfully enabled!',
          severity: 'success'
        });
      }
    } catch (error) {
      console.error('Error enabling WhatsApp:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || 'Failed to enable WhatsApp',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };
  
  const handleDisableWhatsApp = async () => {
    setLoading(true);
    
    try {
      const response = await disableWhatsApp(currentUser.uid);
      
      if (response.data.success) {
        // Update the local settings
        setSettings({
          ...settings,
          whatsapp_enabled: false,
          whatsapp_virtual_number: null,
          whatsapp_display_name: null
        });
        
        setSnackbar({
          open: true,
          message: 'WhatsApp successfully disabled',
          severity: 'success'
        });
      }
    } catch (error) {
      console.error('Error disabling WhatsApp:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || 'Failed to disable WhatsApp',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };
  
  const openEnableDialog = () => {
    setDisplayName(settings?.company_name || '');
    setDialogOpen(true);
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        <WhatsAppIcon sx={{ mr: 1 }} />
        WhatsApp Integration
      </Typography>
      
      {whatsappEnabled ? (
        <Box>
          <Alert severity="success" sx={{ mb: 3 }}>
            <Box display="flex" alignItems="center">
              <CheckCircleIcon sx={{ mr: 1 }} />
              <Typography variant="body1" fontWeight="bold">
                WhatsApp is enabled for your account
              </Typography>
            </Box>
          </Alert>
          
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                <PhoneIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Your WhatsApp Number
              </Typography>
              
              <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
                {virtualNumber}
              </Typography>
              
              <Typography variant="body2" color="text.secondary">
                This is your virtual WhatsApp number that customers will see when you message them.
                Share this number with your customers so they can contact you via WhatsApp.
              </Typography>
            </CardContent>
          </Card>
          
          <Box mt={2}>
            <Typography variant="body2" gutterBottom>
              <strong>Important:</strong> All WhatsApp messages sent through this number will be routed to your FixMatePro dashboard.
            </Typography>
            
            <Box mt={3}>
              <Button
                variant="outlined"
                color="error"
                onClick={handleDisableWhatsApp}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : null}
              >
                {loading ? 'Disabling...' : 'Disable WhatsApp'}
              </Button>
            </Box>
          </Box>
        </Box>
      ) : (
        <Box>
          <Alert severity="info" sx={{ mb: 3 }}>
            Enable WhatsApp to communicate with your customers directly through the FixMatePro platform.
          </Alert>
          
          <Typography variant="body1" paragraph>
            With WhatsApp integration, you can:
          </Typography>
          
          <ul>
            <li>Receive customer inquiries directly in your dashboard</li>
            <li>Send quotes and appointment confirmations via WhatsApp</li>
            <li>Maintain all customer conversations in one place</li>
            <li>Build better relationships with your customers</li>
          </ul>
          
          <Box mt={3}>
            <Button
              variant="contained"
              color="primary"
              onClick={openEnableDialog}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <WhatsAppIcon />}
            >
              {loading ? 'Enabling...' : 'Enable WhatsApp'}
            </Button>
          </Box>
        </Box>
      )}
      
      {/* Dialog for enabling WhatsApp */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>Enable WhatsApp Integration</DialogTitle>
        <DialogContent>
          <Typography variant="body2" paragraph sx={{ mt: 1 }}>
            You'll be assigned a virtual WhatsApp number that customers can use to contact you.
          </Typography>
          
          <TextField
            autoFocus
            margin="dense"
            label="Business Display Name"
            type="text"
            fullWidth
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            helperText="This name will be displayed to customers in WhatsApp messages"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleEnableWhatsApp} 
            variant="contained" 
            color="primary"
            disabled={!displayName.trim()}
          >
            Enable WhatsApp
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
}
